import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Circle as CircleIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SortableTableCell, { toggleSort, sortRows, SortDirection } from '../components/SortableTableCell';

interface Question {
  _id?: string;
  text: string;
  type: 'single_choice';
  options: Array<{
    _id?: string;
    text: string;
    isCorrect: boolean;
    explanation?: string;
  }>;
  difficulty: 'easy' | 'medium' | 'hard';
  points: number;
  order: number;
}

interface Evaluation {
  _id: string;
  id?: string;
  title: string;
  description: string;
  category: string;
  questions: Question[];
  settings: {
    timeLimit: number;
    passThreshold: number;
    maxAttempts: number;
    shuffleQuestions: boolean;
    shuffleOptions: boolean;
    showCorrectAnswers: boolean;
    showResultsImmediately: boolean;
    allowReview: boolean;
  };
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const AdminEvaluations: React.FC = () => {
  const { user } = useAuth();
  const canManage = user?.role === 'admin';
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('title');
  const [order, setOrder] = useState<SortDirection>('asc');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState<Evaluation | null>(null);
  const [viewEvaluation, setViewEvaluation] = useState<Evaluation | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    timeLimit: 30,
    passThreshold: 100,
    maxAttempts: 3,
    shuffleQuestions: true,
    shuffleOptions: true,
    showCorrectAnswers: true,
    showResultsImmediately: true,
    allowReview: true,
    isActive: true
  });

  // Questions state
  const [questions, setQuestions] = useState<Question[]>([]);

  const categories = [
    'phishing_prevention',
    'ransomware_protection', 
    'social_engineering',
    'device_security',
    'password_management',
    'data_protection',
    'email_security',
    'web_browsing',
    'mobile_security',
    'incident_response',
    'general_security'
  ];

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      'phishing_prevention': 'Prevención de Phishing',
      'ransomware_protection': 'Protección Ransomware',
      'social_engineering': 'Ingeniería Social',
      'device_security': 'Seguridad de Dispositivos',
      'password_management': 'Gestión de Contraseñas',
      'data_protection': 'Protección de Datos',
      'email_security': 'Seguridad de Email',
      'web_browsing': 'Navegación Web',
      'mobile_security': 'Seguridad Móvil',
      'incident_response': 'Respuesta a Incidentes',
      'general_security': 'Seguridad General'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' } = {
      'phishing_prevention': 'error',
      'ransomware_protection': 'error',
      'social_engineering': 'warning',
      'device_security': 'primary',
      'password_management': 'info',
      'data_protection': 'success',
      'email_security': 'primary',
      'web_browsing': 'info',
      'mobile_security': 'primary',
      'incident_response': 'warning',
      'general_security': 'info'
    };
    return colors[category] || 'info';
  };

  useEffect(() => {
    fetchEvaluations();
  }, []);

  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      const response = await api.getEvaluations();
      setEvaluations(response.evaluations as any[] || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando evaluaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (evaluation?: Evaluation) => {
    if (evaluation) {
      setEditingEvaluation(evaluation);
      setFormData({
        title: evaluation.title,
        description: evaluation.description,
        category: evaluation.category,
        timeLimit: evaluation.settings.timeLimit,
        passThreshold: evaluation.settings.passThreshold,
        maxAttempts: evaluation.settings.maxAttempts,
        shuffleQuestions: evaluation.settings.shuffleQuestions,
        shuffleOptions: evaluation.settings.shuffleOptions,
        showCorrectAnswers: evaluation.settings.showCorrectAnswers,
        showResultsImmediately: evaluation.settings.showResultsImmediately,
        allowReview: evaluation.settings.allowReview,
        isActive: evaluation.isActive
      });
      setQuestions([...evaluation.questions]);
    } else {
      setEditingEvaluation(null);
      setFormData({
        title: '',
        description: '',
        category: '',
        timeLimit: 30,
        passThreshold: 100,
        maxAttempts: 3,
        shuffleQuestions: true,
        shuffleOptions: true,
        showCorrectAnswers: true,
        showResultsImmediately: true,
        allowReview: true,
        isActive: true
      });
      setQuestions([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvaluation(null);
  };

  const handleSubmit = async () => {
    try {
      // Validaciones del frontend
      if (!formData.title || !formData.description || !formData.category) {
        setSnackbar({ open: true, message: 'Completa todos los campos obligatorios', severity: 'error' });
        return;
      }
      
      if (questions.length === 0) {
        setSnackbar({ 
          open: true, 
          message: 'Debe agregar al menos una pregunta', 
          severity: 'error' 
        });
        return;
      }

      // Validar que todas las preguntas tengan texto y opciones válidas
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        if (!question.text.trim()) {
          setSnackbar({ 
            open: true, 
            message: `La pregunta ${i + 1} debe tener texto`, 
            severity: 'error' 
          });
          return;
        }
        
        if (!question.options || question.options.length < 2) {
          setSnackbar({ 
            open: true, 
            message: `La pregunta ${i + 1} debe tener al menos 2 opciones`, 
            severity: 'error' 
          });
          return;
        }

        // Validar que las opciones tengan texto
        for (let j = 0; j < question.options.length; j++) {
          if (!question.options[j].text.trim()) {
            setSnackbar({ 
              open: true, 
              message: `La opción ${j + 1} de la pregunta ${i + 1} debe tener texto`, 
              severity: 'error' 
            });
            return;
          }
        }

        // Validar que haya exactamente una respuesta correcta
        const correctOptions = question.options.filter(option => option.isCorrect);
        if (correctOptions.length !== 1) {
          setSnackbar({ 
            open: true, 
            message: `La pregunta ${i + 1} debe tener exactamente una respuesta correcta`, 
            severity: 'error' 
          });
          return;
        }
      }

      const evaluationData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        questions: questions.map((q, index) => ({
          text: q.text.trim(),
          type: q.type || 'single_choice',
          options: q.options.map(option => ({
            text: option.text.trim(),
            isCorrect: option.isCorrect,
            explanation: option.explanation?.trim() || ''
          })),
          difficulty: q.difficulty || 'medium',
          points: q.points || 1,
          order: index + 1
        })),
        settings: {
          timeLimit: Number(formData.timeLimit) || 30,
          passThreshold: Number(formData.passThreshold) || 100,
          maxAttempts: Number(formData.maxAttempts) || 3,
          shuffleQuestions: Boolean(formData.shuffleQuestions),
          shuffleOptions: Boolean(formData.shuffleOptions),
          showCorrectAnswers: Boolean(formData.showCorrectAnswers),
          showResultsImmediately: Boolean(formData.showResultsImmediately),
          allowReview: Boolean(formData.allowReview)
        },
        isActive: Boolean(formData.isActive)
      };

      console.log('Enviando datos de evaluación:', evaluationData);
      console.log('Datos JSON:', JSON.stringify(evaluationData, null, 2));

      if (editingEvaluation) {
        const evaluationId = editingEvaluation.id || editingEvaluation._id;
        console.log('Actualizando evaluación con ID:', evaluationId);
        console.log('Tipo de ID:', typeof evaluationId);
        console.log('URL de la petición:', `/evaluations/${evaluationId}`);
        
        const response = await api.updateEvaluation(evaluationId, evaluationData);
        console.log('Respuesta del servidor:', response);
        setSnackbar({ open: true, message: 'Evaluación actualizada exitosamente', severity: 'success' });
      } else {
        console.log('Creando nueva evaluación');
        const response = await api.createEvaluation(evaluationData);
        console.log('Respuesta del servidor:', response);
        setSnackbar({ open: true, message: 'Evaluación creada exitosamente', severity: 'success' });
      }

      handleCloseDialog();
      fetchEvaluations();
    } catch (err: any) {
      console.error('Error completo:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Error message:', err.message);
      
      let errorMessage = 'Error guardando evaluación';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.status === 400) {
        errorMessage = 'Datos inválidos. Verifica que todos los campos estén completos.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Evaluación no encontrada.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Error interno del servidor. Intenta nuevamente.';
      } else if (err.code === 'NETWORK_ERROR') {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Tiempo de espera agotado. Intenta nuevamente.';
      }
      
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    }
  };

  const handleToggleStatus = async (evaluation: Evaluation) => {
    try {
      await api.updateEvaluationStatus(evaluation.id || evaluation._id, !evaluation.isActive);
      setSnackbar({ 
        open: true, 
        message: `Evaluación ${evaluation.isActive ? 'desactivada' : 'activada'} exitosamente`, 
        severity: 'success' 
      });
      fetchEvaluations();
    } catch (err: any) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Error actualizando estado', 
        severity: 'error' 
      });
    }
  };

  const handleDeleteEvaluation = async (evaluation: Evaluation) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta evaluación?')) {
      try {
        await api.deleteEvaluation(evaluation.id || evaluation._id);
        setSnackbar({ open: true, message: 'Evaluación eliminada exitosamente', severity: 'success' });
        fetchEvaluations();
      } catch (err: any) {
        setSnackbar({ 
          open: true, 
          message: err.response?.data?.message || 'Error eliminando evaluación', 
          severity: 'error' 
        });
      }
    }
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      text: '',
      type: 'single_choice',
      options: [
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false }
      ],
      difficulty: 'medium',
      points: 1,
      order: questions.length + 1
    };
    setQuestions([...questions, newQuestion]);
  };

  const removeQuestion = (index: number) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions.map((q, i) => ({ ...q, order: i + 1 })));
  };

  const updateQuestion = (index: number, field: keyof Question, value: any) => {
    const newQuestions = [...questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setQuestions(newQuestions);
  };

  const updateQuestionOption = (questionIndex: number, optionIndex: number, field: keyof Question['options'][0], value: any) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].options[optionIndex] = {
      ...newQuestions[questionIndex].options[optionIndex],
      [field]: value
    };
    setQuestions(newQuestions);
  };

  const setCorrectAnswer = (questionIndex: number, optionIndex: number) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].options.forEach((option, index) => {
      option.isCorrect = index === optionIndex;
    });
    setQuestions(newQuestions);
  };

  const handleRequestSort = (sortKey: string) => {
    const next = toggleSort(orderBy, order, sortKey);
    setOrderBy(next.orderBy);
    setOrder(next.order);
  };

  const displayedEvaluations = useMemo(() => {
    let list = [...evaluations];
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (e) =>
          e.title?.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q)
      );
    }
    if (categoryFilter !== 'all') {
      list = list.filter((e) => e.category === categoryFilter);
    }
    if (statusFilter === 'active') list = list.filter((e) => e.isActive);
    if (statusFilter === 'inactive') list = list.filter((e) => !e.isActive);

    return sortRows(list, orderBy, order, {
      title: (e) => e.title || '',
      category: (e) => e.category || '',
      questions: (e) => e.questions?.length || 0,
      status: (e) => (e.isActive ? 1 : 0),
      passThreshold: (e) => e.settings?.passThreshold ?? 0
    });
  }, [evaluations, searchTerm, categoryFilter, statusFilter, orderBy, order]);

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Cargando evaluaciones...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                {canManage ? 'Gestión de Evaluaciones' : 'Preguntas de los Cursos'}
              </Typography>
              {!canManage && (
                <Typography variant="body2" color="text.secondary">
                  Consulta las preguntas y respuestas de las evaluaciones asociadas a los cursos
                </Typography>
              )}
            </Box>
          </Box>
          {canManage && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              size="large"
            >
              Nueva Evaluación
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
                gap: 2,
                mb: 3
              }}
            >
              <TextField
                label="Buscar evaluaciones"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                fullWidth
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Categoría</InputLabel>
                <Select
                  label="Categoría"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {getCategoryLabel(cat)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Activas</MenuItem>
                  <MenuItem value="inactive">Inactivas</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {displayedEvaluations.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography variant="h6" color="text.secondary">
                  No se encontraron evaluaciones
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <SortableTableCell label="Evaluación" sortKey="title" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell label="Categoría" sortKey="category" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell label="Preguntas" sortKey="questions" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <TableCell>Configuración</TableCell>
                      <SortableTableCell label="Estado" sortKey="status" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <TableCell>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedEvaluations.map((evaluation) => (
                      <TableRow key={evaluation._id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {evaluation.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {evaluation.description}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getCategoryLabel(evaluation.category)}
                            color={getCategoryColor(evaluation.category) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {evaluation.questions.length} preguntas
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              ⏱️ {evaluation.settings.timeLimit} min
                            </Typography>
                            <Typography variant="body2">
                              📊 {evaluation.settings.passThreshold}% aprobar
                            </Typography>
                            <Typography variant="body2">
                              🔄 {evaluation.settings.maxAttempts} intentos
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={evaluation.isActive ? 'Activa' : 'Inactiva'}
                            color={evaluation.isActive ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Ver preguntas">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => setViewEvaluation(evaluation)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            {canManage && (
                              <>
                                <Tooltip title="Editar">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleOpenDialog(evaluation)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={evaluation.isActive ? 'Desactivar' : 'Activar'}>
                                  <IconButton 
                                    size="small" 
                                    color={evaluation.isActive ? 'error' : 'success'}
                                    onClick={() => handleToggleStatus(evaluation)}
                                  >
                                    {evaluation.isActive ? <CancelIcon /> : <CheckCircleIcon />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleDeleteEvaluation(evaluation)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Dialog de creación/edición de evaluación */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          {editingEvaluation ? 'Editar Evaluación' : 'Nueva Evaluación'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Título"
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={formData.category || ''}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Categoría"
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {getCategoryLabel(category)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Descripción"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Configuración
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
            <TextField
              fullWidth
              label="Tiempo límite (minutos)"
              type="number"
              value={formData.timeLimit || ''}
              onChange={(e) => setFormData({ ...formData, timeLimit: parseInt(e.target.value) })}
              inputProps={{ min: 1, max: 120 }}
            />
            <TextField
              fullWidth
              label="Umbral de aprobación (%)"
              type="number"
              value={formData.passThreshold || ''}
              onChange={(e) => setFormData({ ...formData, passThreshold: parseInt(e.target.value) })}
              inputProps={{ min: 0, max: 100 }}
            />
            <TextField
              fullWidth
              label="Intentos máximos"
              type="number"
              value={formData.maxAttempts || ''}
              onChange={(e) => setFormData({ ...formData, maxAttempts: parseInt(e.target.value) })}
              inputProps={{ min: 1, max: 10 }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.shuffleQuestions}
                  onChange={(e) => setFormData({ ...formData, shuffleQuestions: e.target.checked })}
                />
              }
              label="Mezclar preguntas"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.shuffleOptions}
                  onChange={(e) => setFormData({ ...formData, shuffleOptions: e.target.checked })}
                />
              }
              label="Mezclar opciones"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.showCorrectAnswers}
                  onChange={(e) => setFormData({ ...formData, showCorrectAnswers: e.target.checked })}
                />
              }
              label="Mostrar respuestas correctas"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.showResultsImmediately}
                  onChange={(e) => setFormData({ ...formData, showResultsImmediately: e.target.checked })}
                />
              }
              label="Mostrar resultados inmediatamente"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.allowReview}
                  onChange={(e) => setFormData({ ...formData, allowReview: e.target.checked })}
                />
              }
              label="Permitir revisión"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Evaluación activa"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Preguntas ({questions.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddCircleIcon />}
              onClick={addQuestion}
            >
              Agregar Pregunta
            </Button>
          </Box>

          {questions.map((question, questionIndex) => (
            <Card key={questionIndex} sx={{ mb: 2, p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Pregunta {questionIndex + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeQuestion(questionIndex)}
                >
                  <RemoveCircleIcon />
                </IconButton>
              </Box>

              <TextField
                fullWidth
                label="Texto de la pregunta"
                value={question.text}
                onChange={(e) => updateQuestion(questionIndex, 'text', e.target.value)}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Dificultad</InputLabel>
                  <Select
                    value={question.difficulty}
                    onChange={(e) => updateQuestion(questionIndex, 'difficulty', e.target.value)}
                    label="Dificultad"
                  >
                    <MenuItem value="easy">Fácil</MenuItem>
                    <MenuItem value="medium">Medio</MenuItem>
                    <MenuItem value="hard">Difícil</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Puntos"
                  type="number"
                  value={question.points}
                  onChange={(e) => updateQuestion(questionIndex, 'points', parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 10 }}
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Opciones de respuesta:
              </Typography>

              {question.options.map((option, optionIndex) => (
                <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <IconButton
                    size="small"
                    color={option.isCorrect ? 'success' : 'default'}
                    onClick={() => setCorrectAnswer(questionIndex, optionIndex)}
                  >
                    {option.isCorrect ? <CheckCircleIcon /> : <CircleIcon />}
                  </IconButton>
                  <TextField
                    fullWidth
                    label={`Opción ${optionIndex + 1}`}
                    value={option.text}
                    onChange={(e) => updateQuestionOption(questionIndex, optionIndex, 'text', e.target.value)}
                    size="small"
                  />
                </Box>
              ))}
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingEvaluation ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de solo lectura: ver preguntas */}
      <Dialog
        open={Boolean(viewEvaluation)}
        onClose={() => setViewEvaluation(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Preguntas — {viewEvaluation?.title}
        </DialogTitle>
        <DialogContent dividers>
          {viewEvaluation && (
            <Box>
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={getCategoryLabel(viewEvaluation.category)}
                  color={getCategoryColor(viewEvaluation.category) as any}
                  size="small"
                />
                <Chip
                  label={`${viewEvaluation.questions.length} preguntas`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Aprobar: ${viewEvaluation.settings.passThreshold}%`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${viewEvaluation.settings.timeLimit} min`}
                  size="small"
                  variant="outlined"
                />
              </Box>
              {viewEvaluation.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {viewEvaluation.description}
                </Typography>
              )}

              {viewEvaluation.questions.length === 0 ? (
                <Alert severity="info">Esta evaluación no tiene preguntas.</Alert>
              ) : (
                viewEvaluation.questions
                  .slice()
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((question, index) => (
                    <Card key={question._id || index} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {index + 1}. {question.text}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                          <Chip label={`${question.points} pts`} size="small" />
                          <Chip
                            label={
                              question.difficulty === 'easy'
                                ? 'Fácil'
                                : question.difficulty === 'hard'
                                  ? 'Difícil'
                                  : 'Media'
                            }
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {question.options.map((option, optIdx) => (
                            <Box
                              component="li"
                              key={option._id || optIdx}
                              sx={{
                                mb: 0.75,
                                color: option.isCorrect ? 'success.main' : 'text.primary',
                                fontWeight: option.isCorrect ? 600 : 400
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                {option.isCorrect ? (
                                  <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.2 }} />
                                ) : (
                                  <CircleIcon fontSize="small" sx={{ mt: 0.2, color: 'text.disabled' }} />
                                )}
                                <Box>
                                  <Typography variant="body2" component="span">
                                    {option.text}
                                  </Typography>
                                  {option.isCorrect && (
                                    <Typography
                                      variant="caption"
                                      color="success.main"
                                      display="block"
                                    >
                                      Respuesta correcta
                                    </Typography>
                                  )}
                                  {option.explanation && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      {option.explanation}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewEvaluation(null)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminEvaluations; 